import { useEffect, useMemo, useRef, useState } from "react";
import { useVenue } from "../lib/venue";
import { joinMailingList } from "../lib/api";
import { pushCtaClick, pushFormInteraction } from "../lib/analytics";
import { resolveFooter } from "../lib/site-footer";

// Bespoke PROMOTER site (websiteMode + PROMOTER archetype), a port of the
// standalone MAS (Midnight Audio Sanctuary) site into the platform: intro
// splash video, auto-advancing crossfade hero, merch grid (external store),
// past-events archive with RA links, mailing capture, next-event poster.
// Entirely driven by appConfig.promoter + footer + branding, so it's
// white-label and live-previews in the admin builder like every other site.

const INK = "#000000";
const PAPER = "#ffffff";
const MUTED = "#888888";
const HAIR = "rgba(255,255,255,0.1)";
const NAV_LABEL = "text-[11px] font-semibold uppercase tracking-[0.22em] transition hover:opacity-60";

function usePromoter() {
  const { data } = useVenue();
  const venue = data?.venue;
  const p = venue?.appConfig?.promoter;
  return {
    venue,
    name: venue?.appName || venue?.name || "",
    splash: p?.splash ?? null,
    slides: (p?.heroSlides ?? []).filter((s) => s.src),
    ticker: p?.ticker || null,
    merch: p?.merch ?? [],
    pastEvents: p?.pastEvents ?? [],
    nextEvent: p?.nextEvent ?? null,
    displayFontUrl: p?.displayFontUrl || null,
    shopUrl: venue?.appConfig?.shopUrl || null,
  };
}

const SECTION_KICKER = "text-[11px] font-semibold uppercase tracking-[0.3em]";

export function PromoterSite() {
  const { venue, name, splash, slides, ticker, merch, pastEvents, nextEvent, displayFontUrl, shopUrl } = usePromoter();
  const venueId = venue?.id;
  const [email, setEmail] = useState("");
  const [signup, setSignup] = useState<"idle" | "sending" | "done" | "error">("idle");

  const footer = venue ? resolveFooter(venue, venue.appConfig?.footer, new Date()) : null;
  const socials = Object.entries(footer?.socials ?? {});

  const subscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!venueId || !email.trim() || signup === "sending") return;
    pushCtaClick("subscribe");
    // noValidate on the form means a malformed address reaches us instead of
    // being silently blocked by the browser's own type="email" validation,
    // otherwise this check (and the fail event below) would never run.
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      pushFormInteraction("fail");
      setSignup("error");
      return;
    }
    setSignup("sending");
    try {
      await joinMailingList(venueId, email.trim(), "promoter-site");
      pushFormInteraction("success");
      setSignup("done");
      setEmail("");
    } catch {
      pushFormInteraction("fail");
      setSignup("error");
    }
  };

  if (!venue) return null;
  const displayFont = displayFontUrl ? '"PromoterDisplay", "Space Grotesk", system-ui, sans-serif' : '"Space Grotesk", system-ui, sans-serif';

  return (
    <div style={{ backgroundColor: INK, color: PAPER, fontFamily: '"Space Grotesk", system-ui, sans-serif' }} className="min-h-[100dvh]">
      {displayFontUrl && (
        <style>{`@font-face{font-family:"PromoterDisplay";src:url("${displayFontUrl}");font-display:swap;}`}</style>
      )}
      {splash?.videoUrl && <IntroSplash videoUrl={splash.videoUrl} posterUrl={splash.posterUrl} />}

      {/* Sticky nav */}
      <nav
        className="sticky top-0 z-40 flex items-center justify-between px-6 py-4"
        style={{ backgroundColor: "rgba(0,0,0,0.85)", backdropFilter: "blur(8px)", borderBottom: `1px solid ${HAIR}` }}
      >
        <a href="#top" className="text-lg font-bold uppercase tracking-[0.18em]" style={{ fontFamily: displayFont }}>
          {name}
        </a>
        <div className="flex items-center gap-6">
          {(merch.length > 0 || shopUrl) && <a href="#merch" className={NAV_LABEL}>Merch</a>}
          {pastEvents.length > 0 && <a href="#events" className={NAV_LABEL}>Events</a>}
          {nextEvent?.posterUrl && <a href="#tickets" className={NAV_LABEL}>Tickets</a>}
          <a href="#signup" className={`${NAV_LABEL} hidden sm:inline`}>Sign up</a>
        </div>
      </nav>

      {/* Hero: auto-advancing crossfade carousel */}
      <HeroCarousel slides={slides} displayFont={displayFont} fallbackTitle={name} />

      {/* Ticker */}
      {ticker && (
        <div className="rb-marquee border-y py-2.5 text-[11px] font-semibold uppercase tracking-[0.3em]" style={{ borderColor: HAIR }} aria-hidden>
          {[0, 1].map((t) => (
            <div key={t} className="rb-marquee__track">
              {Array.from({ length: 8 }).map((_, i) => (
                <span key={i} className="mx-8 inline-flex items-center gap-8">
                  <span>{ticker}</span>
                  <span className="opacity-40">✦</span>
                </span>
              ))}
            </div>
          ))}
        </div>
      )}

      {/* Merch */}
      {merch.length > 0 && (
        <section id="merch" className="mx-auto max-w-6xl scroll-mt-16 px-6 py-20 sm:py-24">
          <p className={SECTION_KICKER} style={{ color: MUTED }}>Wear the sanctuary</p>
          <h2 className="mt-2 text-2xl font-bold uppercase tracking-tight sm:text-3xl" style={{ fontFamily: displayFont }}>Merch</h2>
          <div className="mt-8 grid grid-cols-2 gap-5 sm:grid-cols-3 lg:grid-cols-4">
            {merch.map((m) => (
              <a key={m.href} href={m.href} target="_blank" rel="noopener noreferrer" className="group block">
                {m.imageUrl && (
                  <div className="aspect-square overflow-hidden" style={{ border: `1px solid ${HAIR}` }}>
                    <img src={m.imageUrl} alt={m.name} className="h-full w-full object-cover transition duration-500 group-hover:scale-[1.03]" />
                  </div>
                )}
                <p className="mt-3 text-sm font-semibold">{m.name}</p>
                {m.price && <p className="text-sm" style={{ color: MUTED }}>{m.price}</p>}
              </a>
            ))}
          </div>
          {shopUrl && (
            <a href={shopUrl} target="_blank" rel="noopener noreferrer" className="mt-8 inline-block border px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.25em] transition hover:bg-white hover:text-black" style={{ borderColor: "rgba(255,255,255,0.4)" }}>
              Full store →
            </a>
          )}
        </section>
      )}

      {/* Past events */}
      {pastEvents.length > 0 && (
        <section id="events" className="mx-auto max-w-6xl scroll-mt-16 px-6 py-20 sm:py-24" style={{ borderTop: `1px solid ${HAIR}` }}>
          <p className={SECTION_KICKER} style={{ color: MUTED }}>Previous events</p>
          <h2 className="mt-2 text-2xl font-bold uppercase tracking-tight sm:text-3xl" style={{ fontFamily: displayFont }}>Selected nights</h2>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {pastEvents.map((ev, i) => {
              const card = (
                <div className="flex h-full flex-col p-5 transition hover:bg-white/[0.04]" style={{ border: `1px solid ${HAIR}` }}>
                  {ev.date && <p className="text-[11px] font-semibold uppercase tracking-[0.2em]" style={{ color: MUTED }}>{ev.date}</p>}
                  <h3 className="mt-2 text-[15px] font-bold leading-snug">{ev.title}</h3>
                  {ev.artists && ev.artists.length > 0 && (
                    <p className="mt-2 text-[13px] leading-relaxed" style={{ color: MUTED }}>{ev.artists.join(" · ")}</p>
                  )}
                  <p className="mt-auto pt-3 text-[11px] uppercase tracking-[0.18em]" style={{ color: MUTED }}>
                    {[ev.venue, ev.city].filter(Boolean).join(", ")}
                    {ev.href && <span className="float-right" style={{ color: PAPER }}>RA ↗</span>}
                  </p>
                </div>
              );
              return ev.href ? (
                <a key={i} href={ev.href} target="_blank" rel="noopener noreferrer" className="block">{card}</a>
              ) : (
                <div key={i}>{card}</div>
              );
            })}
          </div>
        </section>
      )}

      {/* Signup */}
      <section id="signup" className="scroll-mt-16 px-6 py-20 text-center sm:py-24" style={{ borderTop: `1px solid ${HAIR}` }}>
        <p className={SECTION_KICKER} style={{ color: MUTED }}>{footer?.mailingListHeading || "First to know"}</p>
        <h2 className="mt-2 text-2xl font-bold uppercase tracking-tight sm:text-3xl" style={{ fontFamily: displayFont }}>
          Join the list
        </h2>
        {footer?.tagline && <p className="mt-2 text-sm" style={{ color: MUTED }}>{footer.tagline}</p>}
        {signup === "done" ? (
          <p className="mt-8 text-sm font-semibold uppercase tracking-[0.25em]">You're in.</p>
        ) : (
          <form onSubmit={subscribe} noValidate className="mx-auto mt-8 flex max-w-md" style={{ border: `1px solid rgba(255,255,255,0.35)` }}>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              aria-label="Email address"
              className="min-w-0 flex-1 border-0 bg-transparent px-4 py-3 text-sm outline-none placeholder:opacity-40"
              style={{ color: PAPER }}
            />
            <button type="submit" disabled={signup === "sending"} className="border-0 px-6 text-[11px] font-bold uppercase tracking-[0.25em] transition hover:opacity-80 disabled:opacity-50" style={{ backgroundColor: PAPER, color: INK }}>
              {signup === "sending" ? "…" : "Join"}
            </button>
          </form>
        )}
        {signup === "error" && <p className="mt-3 text-xs text-red-400">Couldn't subscribe, try again.</p>}
      </section>

      {/* Next event / tickets */}
      {nextEvent?.posterUrl && (
        <section id="tickets" className="mx-auto max-w-4xl scroll-mt-16 px-6 py-20 sm:py-24" style={{ borderTop: `1px solid ${HAIR}` }}>
          <p className={SECTION_KICKER} style={{ color: MUTED }}>Up next</p>
          <h2 className="mt-2 text-2xl font-bold uppercase tracking-tight sm:text-3xl" style={{ fontFamily: displayFont }}>Get tickets</h2>
          <div className="relative mt-8 overflow-hidden" style={{ border: `1px solid ${HAIR}` }}>
            <img src={nextEvent.posterUrl} alt={nextEvent.title ?? "Next event"} className="w-full" />
            <div className="absolute inset-x-0 bottom-0 p-6" style={{ background: "linear-gradient(transparent, rgba(0,0,0,0.92))" }}>
              {nextEvent.title && <h3 className="text-lg font-bold leading-snug sm:text-xl">{nextEvent.title}</h3>}
              {nextEvent.meta && <p className="mt-1 text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>{nextEvent.meta}</p>}
              {nextEvent.ticketsUrl && (
                <a href={nextEvent.ticketsUrl} target="_blank" rel="noopener noreferrer" className="mt-4 inline-block px-6 py-2.5 text-[11px] font-bold uppercase tracking-[0.25em] transition hover:opacity-85" style={{ backgroundColor: PAPER, color: INK }}>
                  {nextEvent.ticketsLabel || "View on RA →"}
                </a>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Footer */}
      <footer className="px-6 py-10" style={{ borderTop: `1px solid ${HAIR}` }}>
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <p className="text-[11px] tracking-widest" style={{ color: MUTED }}>{footer?.copyright}</p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
            {socials.map(([k, href]) => (
              <a key={k} href={href} target="_blank" rel="noopener noreferrer" className="text-[11px] font-semibold uppercase tracking-[0.22em] transition hover:opacity-60">
                {k}
              </a>
            ))}
            {(footer?.links ?? []).map((l) => (
              <a key={l.label} href={l.href} className="text-[11px] uppercase tracking-[0.18em] transition hover:opacity-60" style={{ color: MUTED }}>
                {l.label}
              </a>
            ))}
          </div>
        </div>
      </footer>
    </div>
  );
}

// Intro splash, plays the brand video once per session, skippable, then fades.
function IntroSplash({ videoUrl, posterUrl }: { videoUrl: string; posterUrl?: string }) {
  const [show, setShow] = useState<boolean>(() => {
    try {
      return !sessionStorage.getItem("assembly:promoterSplashSeen");
    } catch {
      return false;
    }
  });
  const [fading, setFading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const dismiss = () => {
    setFading(true);
    try {
      sessionStorage.setItem("assembly:promoterSplashSeen", "1");
    } catch {
      /* ignore */
    }
    setTimeout(() => setShow(false), 400);
  };

  useEffect(() => {
    if (!show) return;
    const v = videoRef.current;
    if (!v) return;
    const onEnded = () => dismiss();
    const onError = () => dismiss();
    v.addEventListener("ended", onEnded, { once: true });
    v.addEventListener("error", onError, { once: true });
    // Belt & braces: never trap the visitor longer than 8s.
    const cap = setTimeout(dismiss, 8000);
    v.play().catch(() => dismiss());
    return () => {
      v.removeEventListener("ended", onEnded);
      v.removeEventListener("error", onError);
      clearTimeout(cap);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  if (!show) return null;
  return (
    <div
      className={`fixed inset-0 z-[60] flex items-center justify-center bg-black transition-opacity duration-300 ${fading ? "opacity-0" : "opacity-100"}`}
      role="dialog"
      aria-label="Intro"
    >
      <video ref={videoRef} muted playsInline preload="auto" poster={posterUrl} className="h-full w-full object-contain">
        <source src={videoUrl} type="video/mp4" />
      </video>
      <button
        type="button"
        onClick={dismiss}
        className="absolute bottom-6 right-6 border bg-transparent px-4 py-1.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-white transition hover:bg-white hover:text-black"
        style={{ borderColor: "rgba(255,255,255,0.4)" }}
      >
        Skip
      </button>
    </div>
  );
}

// Crossfade hero, no carousel library: slides stack absolutely, the active one
// is opaque, a timer advances. Videos play only while active.
function HeroCarousel({
  slides,
  displayFont,
  fallbackTitle,
}: {
  slides: { type?: "image" | "video"; src: string; poster?: string; title?: string; subtitle?: string }[];
  displayFont: string;
  fallbackTitle: string;
}) {
  const [active, setActive] = useState(0);
  const count = slides.length;
  useEffect(() => {
    if (count < 2) return;
    const t = setInterval(() => setActive((a) => (a + 1) % count), 6000);
    return () => clearInterval(t);
  }, [count]);

  const current = useMemo(() => slides[active] ?? null, [slides, active]);

  if (count === 0) {
    return (
      <section id="top" className="flex min-h-[60vh] items-center justify-center px-6 text-center">
        <h1 className="text-4xl font-bold uppercase tracking-tight sm:text-6xl" style={{ fontFamily: displayFont }}>{fallbackTitle}</h1>
      </section>
    );
  }

  return (
    <section id="top" className="relative h-[82vh] overflow-hidden">
      {slides.map((s, i) => (
        <div key={s.src + i} className={`absolute inset-0 transition-opacity duration-1000 ${i === active ? "opacity-100" : "opacity-0"}`} aria-hidden={i !== active}>
          {s.type === "video" ? (
            <HeroVideo src={s.src} poster={s.poster} active={i === active} />
          ) : (
            <img src={s.src} alt={s.title ?? ""} className="h-full w-full object-cover" />
          )}
        </div>
      ))}
      <div className="absolute inset-0" style={{ background: "linear-gradient(transparent 55%, rgba(0,0,0,0.85))" }} aria-hidden />
      <div className="absolute inset-x-0 bottom-0 p-6 sm:p-10">
        {current?.title && (
          <h1 className="text-3xl font-bold uppercase leading-[1.05] tracking-tight sm:text-5xl" style={{ fontFamily: displayFont, textWrap: "balance" as never }}>
            {current.title}
          </h1>
        )}
        {current?.subtitle && <p className="mt-2 max-w-xl text-sm sm:text-base" style={{ color: "rgba(255,255,255,0.75)" }}>{current.subtitle}</p>}
        {count > 1 && (
          <div className="mt-5 flex gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setActive(i)}
                aria-label={`Slide ${i + 1}`}
                className="h-[3px] w-10 border-0 p-0 transition"
                style={{ backgroundColor: i === active ? "#fff" : "rgba(255,255,255,0.25)" }}
              />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function HeroVideo({ src, poster, active }: { src: string; poster?: string; active: boolean }) {
  const ref = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    if (active) v.play().catch(() => {});
    else v.pause();
  }, [active]);
  return (
    <video ref={ref} muted loop playsInline preload="metadata" poster={poster} className="h-full w-full object-cover">
      <source src={src} type="video/mp4" />
    </video>
  );
}
